import { useMemo } from "react";
import { useResource } from "@/hooks/useResource";
import { campaignsService } from "@/services/campaignsService";
import type { CampaignRun } from "@/types/campaign";
import { displayRunStatus } from "./campaignConstants";

/** Run statuses that mean the dispatch is still in flight. */
export const IN_FLIGHT = new Set(["QUEUED", "RUNNING"]);

/** The outcomes a finished run can have — what the tiles filter the list by. */
export type Outcome = "COMPLETED" | "PARTIAL" | "FAILED";

interface Tally {
  total: number;
  completed: number;
  partial: number;
  failed: number;
  running: number;
  /** Recipients that failed across every run — not the count of failed runs. */
  failedRecipients: number;
}

function tallyRuns(runs: CampaignRun[], total: number): Tally {
  const counted = { completed: 0, partial: 0, failed: 0, running: 0, failedRecipients: 0 };
  for (const run of runs) {
    const status = displayRunStatus(run);
    if (status === "COMPLETED") counted.completed += 1;
    else if (status === "PARTIAL") counted.partial += 1;
    else if (status === "FAILED") counted.failed += 1;
    else if (IN_FLIGHT.has(status)) counted.running += 1;
    counted.failedRecipients += run.failed_count ?? 0;
  }
  return { total, ...counted };
}

/**
 * A campaign's run history, fetched once and shared: the drawer badges its
 * Delivery tab from the same tally the section below renders, so opening the
 * drawer costs one request rather than one per consumer.
 */
export function useCampaignRuns(campaignId: number | null, refreshKey = 0) {
  const { data, loading, error } = useResource(
    () => (campaignId != null ? campaignsService.listAllRuns(campaignId) : Promise.resolve({ runs: [], total: 0 })),
    [campaignId, refreshKey],
  );
  const runs = useMemo(() => data?.runs ?? [], [data]);
  const tally = useMemo(() => tallyRuns(runs, data?.total ?? runs.length), [runs, data?.total]);
  return { runs, tally, loading, error };
}

export type CampaignRunsState = ReturnType<typeof useCampaignRuns>;

